import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import { DataService, BleWifiDeviceRecord, BleWifiQueryRequest, BleWifiQueryResponse } from '../../../core/services/data.service';
import { Subscription, interval } from 'rxjs';

@Component({
  selector: 'app-ble-wifi-devices',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ble-wifi-devices.component.html',
  styleUrl: './ble-wifi-devices.component.css'
})
export class BleWifiDevicesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() floorMapEntity: any = {};
  @Input() buildingPerimeterIds: (string | number)[] = [];
  @Input() incidentId: string | number | null = null;
  @Input() active: boolean = false;
  @Output() devicesChange = new EventEmitter<BleWifiDeviceRecord[]>();
  @Output() deviceSelect = new EventEmitter<BleWifiDeviceRecord>();
  readonly Math = Math;

  records: BleWifiDeviceRecord[] = [];
  total: number = 0;
  limit: number = 10000;
  offset: number = 0;
  page: number = 1;
  totalPages: number = 1;

  isLoading: boolean = false;
  isError: boolean = false;
  errorMessage: string = '';

  // Search & Filters
  searchQuery: string = '';
  platformFilter: string = 'ALL';   // 'ALL' | 'iOS' | 'Android' | 'Other'
  transportFilter: string = 'ALL';  // 'ALL' | 'BLE' | 'WiFi'
  datePreset: string = '5min';      // '5min' | '10min' | '30min' | '1hour' | 'all'

  lastSeenFrom: string = '';
  lastSeenTo: string = '';
  customFromDate: string = '';
  customToDate: string = '';

  sortBy: string = 'last_seen';
  sortOrder: string = 'DESC';

  expandedDeviceId: string | number | null = null;
  copiedField: string = '';

  // Auto refresh
  autoRefresh: boolean = true;
  private autoRefreshSub?: Subscription;

  constructor(
    private dataService: DataService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initDefaultDates();
    if (this.active) {
      this.fetchDevices();
      this.startAutoRefresh();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const activeChanged = changes['active'] && changes['active'].currentValue;
    const perimetersChanged = changes['buildingPerimeterIds'] &&
      JSON.stringify(changes['buildingPerimeterIds'].currentValue) !== JSON.stringify(changes['buildingPerimeterIds'].previousValue);
    const incidentChanged = changes['incidentId'] &&
      changes['incidentId'].currentValue !== changes['incidentId'].previousValue;

    if (this.active && (activeChanged || perimetersChanged || incidentChanged)) {
      this.calculateDateRange(this.datePreset);
      this.fetchDevices();
      this.startAutoRefresh();
    }

    if (changes['active'] && !changes['active'].currentValue) {
      this.stopAutoRefresh();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  initDefaultDates(): void {
    this.setDatePreset('5min', false);
  }

  setDatePreset(preset: string, triggerFetch: boolean = true): void {
    this.datePreset = preset;
    this.calculateDateRange(preset);

    if (triggerFetch) {
      this.page = 1;
      this.fetchDevices();
    }
  }

  calculateDateRange(preset: string): void {
    const now = moment();

    switch (preset) {
      case '5min':
        this.lastSeenFrom = now.clone().subtract(5, 'minutes').toISOString();
        this.lastSeenTo = now.clone().toISOString();
        break;
      case '10min':
        this.lastSeenFrom = now.clone().subtract(10, 'minutes').toISOString();
        this.lastSeenTo = now.clone().toISOString();
        break;
      case '30min':
        this.lastSeenFrom = now.clone().subtract(30, 'minutes').toISOString();
        this.lastSeenTo = now.clone().toISOString();
        break;
      case '1hour':
      case '1hr':
        this.lastSeenFrom = now.clone().subtract(1, 'hour').toISOString();
        this.lastSeenTo = now.clone().toISOString();
        break;
      case 'all':
        this.lastSeenFrom = '';
        this.lastSeenTo = '';
        break;
      default:
        this.lastSeenFrom = now.clone().subtract(5, 'minutes').toISOString();
        this.lastSeenTo = now.clone().toISOString();
        break;
    }
  }

  applyCustomDateRange(): void {
    if (this.customFromDate && this.customToDate) {
      this.lastSeenFrom = moment(this.customFromDate).startOf('day').toISOString();
      this.lastSeenTo = moment(this.customToDate).endOf('day').toISOString();
      this.page = 1;
      this.fetchDevices();
    }
  }

  toggleAutoRefresh(): void {
    this.autoRefresh = !this.autoRefresh;
    if (this.autoRefresh) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();
    if (!this.autoRefresh) return;
    this.autoRefreshSub = interval(10000).subscribe(() => {
      if (this.active && !this.isLoading) {
        if (this.datePreset !== 'all') {
          this.calculateDateRange(this.datePreset);
        }
        this.fetchDevices(false);
      }
    });
  }

  stopAutoRefresh(): void {
    if (this.autoRefreshSub) {
      this.autoRefreshSub.unsubscribe();
      this.autoRefreshSub = undefined;
    }
  }

  fetchDevices(showLoader: boolean = true): void {
    if (showLoader) {
      this.isLoading = true;
    }
    this.isError = false;
    this.errorMessage = '';

    const payload: BleWifiQueryRequest = {
      perimeter_id: this.buildingPerimeterIds && this.buildingPerimeterIds.length > 0
        ? this.buildingPerimeterIds
        : [],
      incident_id: this.incidentId !== null && this.incidentId !== undefined && this.incidentId !== ''
        ? this.incidentId
        : null,
      sort_by: this.sortBy,
      sort_order: this.sortOrder,
      limit: this.limit
    };

    if (this.lastSeenFrom) {
      payload.last_seen_from = this.lastSeenFrom;
    }
    if (this.lastSeenTo) {
      payload.last_seen_to = this.lastSeenTo;
    }

    this.dataService.queryBleWifiDevices(payload).subscribe({
      next: (res: BleWifiQueryResponse) => {
        this.isLoading = false;
        this.isError = false;
        if (res && res.data) {
          this.records = res.data.records || [];
          this.total = res.data.total ?? this.records.length;
          this.limit = res.data.limit ?? this.limit;
          this.offset = res.data.offset ?? 0;
          this.page = res.data.page ?? this.page;
          this.totalPages = Math.max(1, Math.ceil(this.total / this.limit));
        } else if (Array.isArray(res)) {
          this.records = res as any;
          this.total = this.records.length;
          this.totalPages = 1;
        } else {
          this.records = [];
          this.total = 0;
          this.totalPages = 1;
        }
        this.devicesChange.emit(this.records);
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isLoading = false;
        this.isError = true;
        this.errorMessage = err?.error?.message || err?.message || 'Failed to query BLE/WiFi sensor data. Ensure sensor service is running.';
        console.error('BLE/WiFi device query failed:', err);
        this.devicesChange.emit([]);
        this.cdr.detectChanges();
      }
    });
  }

  onSelectDevice(device: BleWifiDeviceRecord, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.deviceSelect.emit(device);
  }

  getFilteredRecords(): BleWifiDeviceRecord[] {
    let list = this.records || [];

    // Filter by transport
    if (this.transportFilter !== 'ALL') {
      list = list.filter(item => {
        const t = (item.transport || item.serviceType || '').toUpperCase();
        return t.includes(this.transportFilter.toUpperCase());
      });
    }

    // Filter by platform
    if (this.platformFilter !== 'ALL') {
      list = list.filter(item => {
        const p = (item.platform || '').toLowerCase();
        if (this.platformFilter === 'iOS') {
          return p.includes('ios') || p.includes('apple');
        }
        if (this.platformFilter === 'Android') {
          return p.includes('android');
        }
        if (this.platformFilter === 'Other') {
          return !p.includes('ios') && !p.includes('apple') && !p.includes('android');
        }
        return true;
      });
    }

    // Filter by search query
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      const q = this.searchQuery.toLowerCase().trim();
      list = list.filter(item => {
        return (item.name && item.name.toLowerCase().includes(q)) ||
          (item.externalDeviceId && item.externalDeviceId.toLowerCase().includes(q)) ||
          (item.address && item.address.toLowerCase().includes(q)) ||
          (item.advertisedMac && item.advertisedMac.toLowerCase().includes(q)) ||
          (item.resolvedMac && item.resolvedMac.toLowerCase().includes(q)) ||
          (item.platform && item.platform.toLowerCase().includes(q)) ||
          (item.transport && item.transport.toLowerCase().includes(q)) ||
          (item.manufacturerId && String(item.manufacturerId).toLowerCase().includes(q)) ||
          (item.perimeterId && String(item.perimeterId).toLowerCase().includes(q)) ||
          (item.host && item.host.toLowerCase().includes(q));
      });
    }

    return list;
  }

  getTransportCount(transport: string): number {
    if (!this.records) return 0;
    const target = transport.toUpperCase();
    return this.records.filter(r => {
      const t = (r.transport || r.serviceType || '').toUpperCase();
      return t.includes(target);
    }).length;
  }

  getPlatformCount(platform: string): number {
    if (!this.records) return 0;
    if (platform === 'iOS') {
      return this.records.filter(r => {
        const p = (r.platform || '').toLowerCase();
        return p.includes('ios') || p.includes('apple');
      }).length;
    }
    if (platform === 'Android') {
      return this.records.filter(r => {
        const p = (r.platform || '').toLowerCase();
        return p.includes('android');
      }).length;
    }
    return 0;
  }

  toggleExpand(deviceId: string | number): void {
    if (this.expandedDeviceId === deviceId) {
      this.expandedDeviceId = null;
    } else {
      this.expandedDeviceId = deviceId;
    }
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.fetchDevices();
    }
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
      this.fetchDevices();
    }
  }

  copyToClipboard(text?: string, fieldName: string = ''): void {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      this.copiedField = `${fieldName}-${text}`;
      setTimeout(() => {
        if (this.copiedField === `${fieldName}-${text}`) {
          this.copiedField = '';
          this.cdr.detectChanges();
        }
      }, 2000);
      this.cdr.detectChanges();
    });
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).format('MMM D, YYYY, h:mm:ss a');
  }

  formatRelative(dateStr?: string): string {
    if (!dateStr) return 'N/A';
    return moment(dateStr).fromNow();
  }

  getRssiQuality(rssi?: number): { label: string; class: string; icon: string; bars: number } {
    if (rssi === undefined || rssi === null) {
      return { label: 'Unknown', class: 'rssi-unknown', icon: 'fa-signal', bars: 0 };
    }
    if (rssi >= -60) {
      return { label: 'Excellent', class: 'rssi-excellent', icon: 'fa-signal', bars: 4 };
    }
    if (rssi >= -75) {
      return { label: 'Good', class: 'rssi-good', icon: 'fa-signal', bars: 3 };
    }
    if (rssi >= -85) {
      return { label: 'Fair', class: 'rssi-fair', icon: 'fa-signal', bars: 2 };
    }
    return { label: 'Weak', class: 'rssi-weak', icon: 'fa-signal', bars: 1 };
  }

  getPlatformIcon(platform?: string): string {
    if (!platform) return 'fa-solid fa-microchip';
    const p = platform.toLowerCase();
    if (p.includes('ios') || p.includes('apple')) return 'fa-brands fa-apple';
    if (p.includes('android')) return 'fa-brands fa-android';
    if (p.includes('windows')) return 'fa-brands fa-windows';
    if (p.includes('linux')) return 'fa-brands fa-linux';
    return 'fa-solid fa-microchip';
  }

  getManufacturerName(manufacturerId?: string): string {
    if (!manufacturerId) return '';
    const id = String(manufacturerId).trim();
    if (id === '6' || id === '0006' || id === '0x0006') return 'Apple, Inc.';
    if (id === '224' || id === '00e0' || id === '0x00e0') return 'Google, Inc.';
    if (id === '117' || id === '0075' || id === '0x0075') return 'Samsung';
    if (id === '89' || id === '0059' || id === '0x0059') return 'Nordic Semiconductor';
    if (id === '343' || id === '0157' || id === '0x0157') return 'Xiaomi';
    return `ID: ${manufacturerId}`;
  }

  hasManufacturerData(device: BleWifiDeviceRecord): boolean {
    return !!(device.manufacturerData && Object.keys(device.manufacturerData).length > 0);
  }

  getManufacturerDataKeys(device: BleWifiDeviceRecord): string[] {
    return device.manufacturerData ? Object.keys(device.manufacturerData) : [];
  }
}
